/**
 * @typedef {'nickname' | 'gf_name' | 'discord_username' | 'real_name' | 'steam_name' | 'steam_id' | 'iracing_name' | 'iracing_id' | 'psn_username' | 'xbox_username' | 'ea_username'} DriverDisplayIdentifier
 */

export const DRIVER_DISPLAY_IDENTIFIER_OPTIONS = [
  { value: 'nickname', label: 'Nickname' },
  { value: 'gf_name', label: 'Grid Finder ID' },
  { value: 'discord_username', label: 'Discord Username' },
  { value: 'real_name', label: 'Real Name' },
  { value: 'steam_name', label: 'Steam Name' },
  { value: 'steam_id', label: 'Steam ID' },
  { value: 'iracing_name', label: 'iRacing Name' },
  { value: 'iracing_id', label: 'iRacing ID' },
  { value: 'psn_username', label: 'PSN Username' },
  { value: 'xbox_username', label: 'Xbox Username' },
  { value: 'ea_username', label: 'EA Username' },
]

function normalizeIdentifierValue(value) {
  if (typeof value === 'number') {
    return `${value}`
  }

  if (typeof value !== 'string') {
    return null
  }

  const trimmedValue = value.trim()

  return trimmedValue.length > 0 ? trimmedValue : null
}

export function getResolvedUser(entity) {
  if (!entity || typeof entity !== 'object') {
    return null
  }

  return entity.user && typeof entity.user === 'object' ? entity.user : entity
}

export function getDriverNickname(entity) {
  const user = getResolvedUser(entity)
  const nicknameSources = [
    entity?.nickname,
    entity?.championshipAttendee?.nickname,
    user?.nickname,
    user?.championshipAttendee?.nickname,
    ...(Array.isArray(user?.attendingChampionships)
      ? user.attendingChampionships.map(
          (attendingChampionship) => attendingChampionship?.nickname,
        )
      : []),
  ]

  for (const nickname of nicknameSources) {
    const normalizedNickname = normalizeIdentifierValue(nickname)

    if (normalizedNickname) return normalizedNickname
  }

  return null
}

export function getUserApplication(entity) {
  const user = getResolvedUser(entity)
  const userApplicationSources = [
    entity?.championshipAttendee?.metadata?.userApplication,
    entity?.metadata?.userApplication,
    user?.championshipAttendee?.metadata?.userApplication,
    user?.metadata?.userApplication,
    ...(Array.isArray(user?.attendingChampionships)
      ? user.attendingChampionships.map(
          (attendingChampionship) =>
            attendingChampionship?.metadata?.userApplication,
        )
      : []),
  ]

  const userApplication = userApplicationSources.find(
    (value) => value && typeof value === 'object',
  )

  return userApplication && typeof userApplication === 'object'
    ? userApplication
    : {}
}

function getConnectedAccounts(entity) {
  const user = getResolvedUser(entity)

  return Array.isArray(user?.connectedAccounts) ? user.connectedAccounts : []
}

function getConnectedAccount(entity, serviceId) {
  return (
    getConnectedAccounts(entity).find(
      (account) => account?.serviceId?.toLowerCase() === serviceId,
    ) || null
  )
}

function normalizeImportedSource(source) {
  const normalizedSource = normalizeIdentifierValue(source)?.toLowerCase()

  switch (normalizedSource) {
    case 'ac':
    case 'acc':
    case 'assetto-corsa':
    case 'assetto-corsa-competizione':
    case 'assetto-corsa-evo':
    case 'assetto_corsa_evo':
    case 'assetto-corsa-rally':
    case 'steam':
      return 'steam'
    case 'iracing':
      return 'iracing'
    case 'trackmania':
    case 'ubisoft':
      return 'ubisoft'
    default:
      return normalizedSource
  }
}

function getSourceIdentifierCandidates(source) {
  const normalizedSource = normalizeImportedSource(source)

  switch (normalizedSource) {
    case 'iracing':
      return ['iracing_name', 'iracing_id']
    case 'steam':
      return ['steam_name', 'steam_id']
    default:
      return []
  }
}

export function getImportedDriverIdentity(entity) {
  const metadataImportedDriver = entity?.metadata?.importedDriver
  const unclaimedDriverMeta = entity?.unclaimedDriverMeta
  const unclaimedDriver = entity?.unclaimedDriver
  const metadata = entity?.metadata || {}
  const source =
    normalizeIdentifierValue(metadataImportedDriver?.source) ||
    normalizeIdentifierValue(unclaimedDriverMeta?.source) ||
    normalizeIdentifierValue(unclaimedDriver?.source) ||
    (metadata.custId ? 'iracing' : null) ||
    (metadata.driverGuid || metadata.playerId ? 'steam' : null)
  const externalId =
    normalizeIdentifierValue(metadataImportedDriver?.externalId) ||
    normalizeIdentifierValue(unclaimedDriverMeta?.externalId) ||
    normalizeIdentifierValue(unclaimedDriver?.externalId) ||
    normalizeIdentifierValue(metadata.custId) ||
    normalizeIdentifierValue(metadata.driverGuid) ||
    normalizeIdentifierValue(metadata.playerId)
  const name =
    normalizeIdentifierValue(metadataImportedDriver?.name) ||
    normalizeIdentifierValue(unclaimedDriverMeta?.name) ||
    normalizeIdentifierValue(unclaimedDriver?.name) ||
    normalizeIdentifierValue(metadata.driverName) ||
    normalizeIdentifierValue(metadata.name) ||
    (!entity?.user?.id
      ? normalizeIdentifierValue(entity?.user?.username)
      : null)

  if (!name && !externalId && !source) {
    return null
  }

  return {
    name,
    externalId,
    source,
    normalizedSource: normalizeImportedSource(source),
  }
}

function getFullName(user) {
  const firstName = normalizeIdentifierValue(user?.firstName)
  const lastName = normalizeIdentifierValue(user?.lastName)
  const fullName = [firstName, lastName].filter(Boolean).join(' ')

  return normalizeIdentifierValue(fullName)
}

export function getDriverDisplayIdentifierValue(entity, identifier) {
  const user = getResolvedUser(entity)
  const userApplication = getUserApplication(entity)
  const steamAccount = getConnectedAccount(entity, 'steam')
  const iracingAccount = getConnectedAccount(entity, 'iracing')
  const importedDriver = getImportedDriverIdentity(entity)

  switch (identifier) {
    case 'nickname':
      return getDriverNickname(entity)
    case 'gf_name':
      return (
        normalizeIdentifierValue(user?.username) ||
        normalizeIdentifierValue(importedDriver?.name)
      )
    case 'discord_username':
      return (
        normalizeIdentifierValue(user?.discordUsername) ||
        normalizeIdentifierValue(userApplication.discord_username)
      )
    case 'real_name':
      return (
        normalizeIdentifierValue(userApplication.real_name) || getFullName(user)
      )
    case 'steam_name':
      return (
        normalizeIdentifierValue(steamAccount?.serviceAccountName) ||
        (importedDriver?.normalizedSource === 'steam'
          ? normalizeIdentifierValue(importedDriver.name)
          : null)
      )
    case 'steam_id':
      return (
        normalizeIdentifierValue(steamAccount?.serviceAccountId) ||
        normalizeIdentifierValue(userApplication.steam_username) ||
        (importedDriver?.normalizedSource === 'steam'
          ? normalizeIdentifierValue(importedDriver.externalId)
          : null)
      )
    case 'iracing_name':
      return (
        normalizeIdentifierValue(iracingAccount?.serviceAccountName) ||
        (importedDriver?.normalizedSource === 'iracing'
          ? normalizeIdentifierValue(importedDriver.name)
          : null)
      )
    case 'iracing_id':
      return (
        normalizeIdentifierValue(iracingAccount?.serviceAccountId) ||
        normalizeIdentifierValue(userApplication.iracing_id) ||
        (importedDriver?.normalizedSource === 'iracing'
          ? normalizeIdentifierValue(importedDriver.externalId)
          : null)
      )
    case 'psn_username':
      return normalizeIdentifierValue(userApplication.psn_username)
    case 'xbox_username':
      return normalizeIdentifierValue(userApplication.xbox_username)
    case 'ea_username':
      return normalizeIdentifierValue(userApplication.ea_username)
    default:
      return null
  }
}

export function getDriverDisplayIdentifierFallbackValue(
  entity,
  { source, excludedIdentifiers = [] } = {},
) {
  const excludedIdentifierSet = new Set(excludedIdentifiers.filter(Boolean))
  const fallbackIdentifiers = [
    'gf_name',
    ...getSourceIdentifierCandidates(source),
  ].filter((fallbackIdentifier, index, identifiers) => {
    return (
      !excludedIdentifierSet.has(fallbackIdentifier) &&
      identifiers.indexOf(fallbackIdentifier) === index
    )
  })

  for (const fallbackIdentifier of fallbackIdentifiers) {
    const fallbackValue = getDriverDisplayIdentifierValue(
      entity,
      fallbackIdentifier,
    )

    if (fallbackValue) {
      return fallbackValue
    }
  }

  return null
}

export function getDriverDisplayIdentifierDisplay(
  entity,
  identifier,
  { source } = {},
) {
  return (
    getDriverDisplayIdentifierValue(entity, identifier) ||
    getDriverDisplayIdentifierFallbackValue(entity, {
      source,
      excludedIdentifiers: [identifier],
    }) ||
    'Not provided'
  )
}

export function isDriverDisplayIdentifierAvailableForSource(
  identifier,
  source,
) {
  const normalizedSource = normalizeIdentifierValue(source)?.toLowerCase()
  const isSteamIdentifier =
    identifier === 'steam_name' || identifier === 'steam_id'
  const isIRacingIdentifier =
    identifier === 'iracing_name' || identifier === 'iracing_id'

  if (!normalizedSource) {
    return true
  }

  if (normalizedSource.includes('iracing')) {
    return !isSteamIdentifier
  }

  return !isIRacingIdentifier
}

export function getAvailableDriverDisplayIdentifierOptions(items = [], source) {
  return DRIVER_DISPLAY_IDENTIFIER_OPTIONS.filter((option) => {
    if (!isDriverDisplayIdentifierAvailableForSource(option.value, source)) {
      return false
    }

    return items.some(
      (item) => getDriverDisplayIdentifierValue(item, option.value) !== null,
    )
  })
}

export function getDefaultDriverDisplayIdentifier({
  source,
  context = 'import',
} = {}) {
  if (context === 'standings') {
    return 'gf_name'
  }

  const normalizedSource = `${source || ''}`.toLowerCase()

  if (normalizedSource === 'ac' || normalizedSource === 'acc') {
    return 'steam_id'
  }

  if (normalizedSource.includes('iracing')) {
    return 'iracing_id'
  }

  return 'gf_name'
}

export function getInitialDriverDisplayIdentifier({
  items = [],
  source,
  context = 'import',
  defaultIdentifier,
}) {
  const availableOptions = getAvailableDriverDisplayIdentifierOptions(
    items,
    source,
  )
  const preferredIdentifier = availableOptions.some(
    (option) => option.value === 'nickname',
  )
    ? 'nickname'
    : defaultIdentifier ||
      getDefaultDriverDisplayIdentifier({
        source,
        context,
      })

  if (availableOptions.some((option) => option.value === preferredIdentifier)) {
    return preferredIdentifier
  }

  return availableOptions[0]?.value || 'gf_name'
}

export function buildAssignUserOptions({
  eventAttendees = [],
  resultId,
  resultMappings = {},
  resultMatchingIdentifier = 'gf_name',
}) {
  const currentUserId = resultMappings[resultId] || null
  const assignedUserIds = new Set(Object.values(resultMappings).filter(Boolean))

  return eventAttendees
    .filter((attendee) => {
      const userId = attendee?.user?.id

      return (
        userId && (!assignedUserIds.has(userId) || userId === currentUserId)
      )
    })
    .map((attendee) => ({
      id: attendee.user.id,
      name: getDriverDisplayIdentifierDisplay(
        attendee,
        resultMatchingIdentifier,
      ),
    }))
}
